if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // V12 optimistic UI — open the drawer with a placeholder line item BEFORE
        // /cart/add.js resolves, then reconcile silently on response. Only enabled
        // for cart-drawer (not cart-notification mini popup) and only if we can
        // extract enough info from the surrounding DOM.
        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        let optimisticApplied = false;
        if (
          this.cart &&
          this.cart.tagName === 'CART-DRAWER' &&
          typeof this.cart.addOptimistic === 'function'
        ) {
          const optimisticPayload = this.buildOptimisticPayload(formData);
          if (optimisticPayload) {
            optimisticApplied = this.cart.addOptimistic(optimisticPayload, requestId);
          }
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (optimisticApplied && this.cart && typeof this.cart.rollbackOptimistic === 'function') {
                this.cart.rollbackOptimistic(requestId, response.description || response.message);
              }
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
                optimisticReconciled: optimisticApplied,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            const finalize = () => {
              CartPerformance.measure('add:paint-updated-sections', () => {
                if (optimisticApplied && typeof this.cart.reconcile === 'function') {
                  this.cart.reconcile(response, requestId);
                } else {
                  this.cart.renderContents(response);
                }
              });
            };
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  requestAnimationFrame(finalize);
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              finalize();
            }
          })
          .catch((e) => {
            if (optimisticApplied && this.cart && typeof this.cart.rollbackOptimistic === 'function') {
              this.cart.rollbackOptimistic(requestId, (window.cartStrings && window.cartStrings.error) || 'Could not add to cart');
            }
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      /* V12 optimistic UI — extract minimal line-item payload from the
       * surrounding DOM (PDP, collection card, or quick-add modal) so the
       * drawer can render a placeholder before the server response.
       * Returns null if extraction fails — caller falls back to legacy flow. */
      buildOptimisticPayload(formData) {
        try {
          const variantId = formData.get('id');
          if (!variantId) return null;
          const quantity = parseInt(formData.get('quantity') || '1', 10) || 1;

          // Look up the surrounding context: PDP, card, or quick-add modal.
          // Prefer scoped queries to avoid grabbing the wrong product on grid pages.
          const scope =
            this.closest('quick-add-modal') ||
            this.closest('product-card, .product-card-wrapper, .card-wrapper, .ct-product-card, .pro_info-main, .pro_info, .product-card_main') ||
            this.closest('.product__info-container, .product-info, main') ||
            document;

          const titleEl =
            scope.querySelector('[data-product-title]') ||
            scope.querySelector('.product__title h1, .product__title') ||
            scope.querySelector('.card__heading a, .card__heading') ||
            scope.querySelector('.prodcut_title, .pro_title');
          const title = titleEl ? titleEl.textContent.trim() : '';

          const linkEl =
            scope.querySelector('.card__heading a, a.full-unstyled-link, a[href*="/products/"]') ||
            scope.querySelector('.pro_info > a, .pro_info-main a[href*="/products/"]');
          const url = linkEl ? linkEl.getAttribute('href') : '';

          let image = '';
          const imgEl =
            scope.querySelector('.product__media img, .product-media img') ||
            scope.querySelector('.card__media img, .media img') ||
            scope.querySelector('.card_pro_img, img.card_pro_img') ||
            scope.querySelector('img[src*="cdn.shopify.com"]');
          if (imgEl) {
            // Prefer a small width for the cart-drawer thumbnail
            const src = imgEl.currentSrc || imgEl.getAttribute('src') || '';
            // Append width=200 if it's a Shopify CDN image without a width param
            if (src && /cdn\.shopify\.com/.test(src) && !/[?&]width=/.test(src)) {
              image = src + (src.includes('?') ? '&' : '?') + 'width=200';
            } else {
              image = src;
            }
          }

          const priceEl =
            scope.querySelector('.price__regular .price-item--regular') ||
            scope.querySelector('.price-item--regular') ||
            scope.querySelector('.price__current') ||
            scope.querySelector('.prodcut_price_secend, .prodcut_price > .prodcut_price_secend');
          const priceFormatted = priceEl ? priceEl.textContent.trim() : '';

          const compareEl =
            scope.querySelector('.price__sale .price-item--regular') ||
            scope.querySelector('.price-item--last') ||
            scope.querySelector('.prodcut_price_first');
          const compareAtFormatted = compareEl ? compareEl.textContent.trim() : '';

          const subtitleEl =
            scope.querySelector('[data-product-subtitle], .product__subtitle') ||
            scope.querySelector('.prodcut_sec_title');
          const subtitle = subtitleEl ? subtitleEl.textContent.trim() : '';

          // Minimal viable payload — must have variantId + at least a title or image
          if (!title && !image) return null;

          return {
            variantId: Number(variantId),
            quantity,
            title,
            url,
            image,
            priceFormatted,
            compareAtFormatted,
            subtitle,
          };
        } catch (e) {
          console.warn('[optimistic] payload extraction failed', e);
          return null;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          const isQuantityError = errorMessage.toLowerCase().includes('maximum quantity') ||
            errorMessage.toLowerCase().includes('already in your cart') ||
            errorMessage.toLowerCase().includes('not enough');

          const displayMessage = isQuantityError
            ? 'This item is already in your cart. Please proceed to checkout.'
            : errorMessage;

          this.errorMessage.textContent = displayMessage;

          // Show as a toast notification instead of inline
          this.showToast(displayMessage);
          this.errorMessageWrapper.setAttribute('hidden', '');
        }
      }

      showToast(message) {
        const existing = document.querySelector('.product-form-toast');
        if (existing) existing.remove();

        const toast = document.createElement('div');
        toast.className = 'product-form-toast';
        toast.innerHTML = `
          <div class="product-form-toast__content">
            <span class="product-form-toast__message">${message}</span>
            <button class="product-form-toast__close" aria-label="Close">&times;</button>
          </div>
        `;
        document.body.appendChild(toast);

        requestAnimationFrame(() => toast.classList.add('product-form-toast--visible'));

        toast.querySelector('.product-form-toast__close').addEventListener('click', () => {
          toast.classList.remove('product-form-toast--visible');
          setTimeout(() => toast.remove(), 300);
        });

        setTimeout(() => {
          if (toast.parentNode) {
            toast.classList.remove('product-form-toast--visible');
            setTimeout(() => toast.remove(), 300);
          }
        }, 5000);
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
